(() => {
  if (window.WaterFunLiveSearchLoaded) return;
  window.WaterFunLiveSearchLoaded = true;

  const MIN_CHARS = 1;
  const DEBOUNCE_MS = 120;
  const INDEX_URL = '/products.json?limit=250';

  let productIndexPromise = null;

  const normalizeHebrew = (value) => {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\u0591-\u05C7]/g, '')
      .replace(/[״׳"']/g, '')
      .replace(/ך/g, 'כ')
      .replace(/ם/g, 'מ')
      .replace(/ן/g, 'נ')
      .replace(/ף/g, 'פ')
      .replace(/ץ/g, 'צ');
  };

  const escapeHtml = (value) => {
    return (value || '').toString().replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[char]));
  };

  const moneyFormat = (price) => {
    if (!price) return '';
    return `₪${Number(price).toLocaleString('he-IL')}`;
  };

  const mapShopifyProduct = (product) => {
    const firstVariant = product.variants && product.variants.length ? product.variants[0] : null;
    const firstImage = product.images && product.images.length ? product.images[0] : null;
    const tagArray = Array.isArray(product.tags) ? product.tags : [];

    return {
      title: product.title || '',
      url: `/products/${product.handle}`,
      image: firstImage ? firstImage.src : null,
      price: firstVariant ? moneyFormat(firstVariant.price) : '',
      available: product.variants ? product.variants.some((variant) => variant.available) : true,
      vendor: product.vendor || '',
      type: product.product_type || '',
      tags: tagArray.join(' '),
      // Lowercased tag list for the free-shipping / pickup-only flag check.
      tagList: tagArray.map((tag) => String(tag).toLowerCase())
    };
  };

  // Reads the tag-gated product-flag config passed from Liquid (theme
  // settings) as data-* attributes on the search root.
  const readFlagConfig = (root) => {
    const truthy = (value) => value === 'true' || value === '1';
    return {
      shipEnable: truthy(root.dataset.flagShipEnable),
      shipTag: (root.dataset.flagShipTag || '').toLowerCase(),
      shipText: root.dataset.flagShipText || 'משלוח חינם',
      pickupEnable: truthy(root.dataset.flagPickupEnable),
      pickupTag: (root.dataset.flagPickupTag || '').toLowerCase(),
      pickupText: root.dataset.flagPickupText || 'איסוף עצמי בלבד',
      cfpEnable: truthy(root.dataset.cfpEnable),
      cfpTag: (root.dataset.cfpTag || '').toLowerCase(),
      cfpText: root.dataset.cfpText || 'להצעת מחיר התקשרו:',
      cfpPhone: root.dataset.cfpPhone || ''
    };
  };

  // "Call for price" message for quote-only products, or '' if it has a price.
  const priceOrCallForPrice = (product, config) => {
    const tags = product.tagList || [];
    if (config && config.cfpEnable && config.cfpTag && tags.includes(config.cfpTag)) {
      const phone = config.cfpPhone ? ' ' + config.cfpPhone : '';
      return `<span class="wf-live-search__price wf-live-search__price--cfp">${escapeHtml(config.cfpText + phone)}</span>`;
    }
    return `<span class="wf-live-search__price">${escapeHtml(product.price || '')}</span>`;
  };

  // Returns the pill markup for a product, or '' if it carries no flag tag.
  // Pickup-only wins over free-shipping (same priority as the Liquid snippet).
  const flagPill = (product, config) => {
    if (!config) return '';
    const tags = product.tagList || [];
    if (config.pickupEnable && config.pickupTag && tags.includes(config.pickupTag)) {
      return `<span class="wf-ship-pill wf-pickup">${escapeHtml(config.pickupText)}</span>`;
    }
    if (config.shipEnable && config.shipTag && tags.includes(config.shipTag)) {
      return `<span class="wf-ship-pill">${escapeHtml(config.shipText)}</span>`;
    }
    return '';
  };

  const loadProductIndex = async () => {
    if (!productIndexPromise) {
      productIndexPromise = fetch(INDEX_URL, {
        headers: { Accept: 'application/json' }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Products JSON request failed: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          const products = Array.isArray(data.products) ? data.products : [];
          return products.map(mapShopifyProduct);
        });
    }

    return productIndexPromise;
  };

  // Mosaic & porcelain catalogue items (2026-09-30). They are not Shopify
  // products, so /products.json does not list them; the theme exposes them
  // as JSON through the Section Rendering API (sections/waterfun-mosaic-
  // index.liquid). Loaded once, on the first search, like the product index.
  // A failure here never breaks product search - it just yields no items.
  //
  // The request names the theme that rendered this page (Shopify.theme.id):
  // a bare "/?section_id=..." can be answered by the PUBLISHED theme while
  // an unpublished theme is being previewed - and a theme without this
  // section returns 404, i.e. no catalogue items in the quick search (bug
  // reported 2026-09-30). On the published theme the parameter is a no-op.
  const mosaicIndexUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('section_id', 'waterfun-mosaic-index');
    const theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.id && theme.role !== 'main') url.searchParams.set('preview_theme_id', theme.id);
    return url.toString();
  };
  const MOSAIC_LIMIT = 5;
  let mosaicIndexPromise = null;

  const loadMosaicIndex = () => {
    if (!mosaicIndexPromise) {
      mosaicIndexPromise = fetch(mosaicIndexUrl(), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Mosaic index request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const node = doc.querySelector('[data-wf-mosaic-index]');
          const items = node ? JSON.parse(node.textContent) : [];
          return items.map((item) => ({
            ...item,
            searchText: normalizeHebrew([item.title, item.sku, item.type, item.material, item.color].join(' '))
          }));
        })
        .catch((error) => {
          console.warn('[WaterFun Live Search] mosaic index', error);
          return [];
        });
    }
    return mosaicIndexPromise;
  };

  const renderMosaicResult = (item) => {
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="64" height="64">`
      : `<div class="wf-live-search__image-placeholder"></div>`;
    const meta = [item.type, item.sku ? `מק״ט ${item.sku}` : ''].filter(Boolean).join(' · ');
    return `
      <li class="wf-live-search__item">
        <a class="wf-live-search__link" href="${escapeHtml(item.url)}">
          <span class="wf-live-search__image">${image}</span>
          <span class="wf-live-search__content">
            <span class="wf-live-search__title">${escapeHtml(item.title)}</span>
            <span class="wf-live-search__meta"><span class="wf-live-search__sub">${escapeHtml(meta)}</span></span>
          </span>
        </a>
      </li>
    `;
  };

  const buildFullSearchUrl = (query) => {
    const url = new URL('/search', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url.toString();
  };

  const productMatches = (product, query) => {
    const normalizedQuery = normalizeHebrew(query);

    const searchableText = normalizeHebrew([
      product.title,
      product.vendor,
      product.type,
      product.tags
    ].filter(Boolean).join(' '));

    return searchableText.includes(normalizedQuery);
  };

  const renderResult = (product, flagConfig) => {
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="64" height="64">`
      : `<div class="wf-live-search__image-placeholder"></div>`;

    const availability = product.available
      ? ''
      : `<span class="wf-live-search__soldout">אזל מהמלאי</span>`;

    const pill = flagPill(product, flagConfig);

    return `
      <li class="wf-live-search__item">
        <a class="wf-live-search__link" href="${escapeHtml(product.url)}">
          <span class="wf-live-search__image">${image}</span>
          <span class="wf-live-search__content">
            <span class="wf-live-search__title">${escapeHtml(product.title)}</span>
            ${pill}
            <span class="wf-live-search__meta">
              ${priceOrCallForPrice(product, flagConfig)}
              ${availability}
            </span>
          </span>
        </a>
      </li>
    `;
  };

  const init = (root) => {
    const form = root.querySelector('[data-wf-live-search-form]');
    const input = root.querySelector('[data-wf-live-search-input]');
    const panel = root.querySelector('[data-wf-live-search-panel]');
    const results = root.querySelector('[data-wf-live-search-results]');
    const state = root.querySelector('[data-wf-live-search-state]');
    const allLink = root.querySelector('[data-wf-live-search-all]');

    if (!form || !input || !panel || !results || !state || !allLink) return;

    const flagConfig = readFlagConfig(root);
    let timer = null;

    const close = () => {
      panel.hidden = true;
      results.innerHTML = '';
      state.textContent = '';
      allLink.hidden = true;
    };

    const open = () => {
      panel.hidden = false;
    };

    const search = async (rawQuery) => {
      const query = rawQuery.trim();

      if (normalizeHebrew(query).length < MIN_CHARS) {
        close();
        return;
      }

      open();
      state.textContent = 'מחפש...';
      results.innerHTML = '';
      allLink.hidden = true;

      try {
        const [products, mosaic] = await Promise.all([loadProductIndex(), loadMosaicIndex()]);

        const matches = products
          .filter((product) => productMatches(product, query))
          .slice(0, 8);

        const normalizedQuery = normalizeHebrew(query);
        const mosaicMatches = mosaic
          .filter((item) => item.searchText.includes(normalizedQuery))
          .slice(0, MOSAIC_LIMIT);

        if (!matches.length && !mosaicMatches.length) {
          state.textContent = 'לא נמצאו מוצרים מתאימים';
          allLink.hidden = false;
          allLink.href = buildFullSearchUrl(query);
          return;
        }

        state.textContent = '';
        // Products first, then a labelled group of catalogue items.
        results.innerHTML =
          matches.map((product) => renderResult(product, flagConfig)).join('') +
          (mosaicMatches.length
            ? `<li class="wf-live-search__group" role="presentation">פסיפס וקרמיקה · מהקטלוג</li>` +
              mosaicMatches.map(renderMosaicResult).join('')
            : '');
        allLink.hidden = false;
        allLink.href = buildFullSearchUrl(query);
      } catch (error) {
        console.warn('[WaterFun Live Search]', error);
        state.textContent = 'לא ניתן לטעון תוצאות כרגע';
        allLink.hidden = false;
        allLink.href = buildFullSearchUrl(query);
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => search(input.value), DEBOUNCE_MS);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_CHARS) {
        search(input.value);
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) close();
    });

    form.addEventListener('submit', (event) => {
      const query = input.value.trim();

      if (!query) {
        event.preventDefault();
        return;
      }

      form.action = buildFullSearchUrl(query);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-wf-live-search]').forEach(init);
  });
})();